import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
  Style,
  Palette,
  Icon,
  Label,
  Material,
  ItemSlot,
  ItemProperties
} from 'configurator-ui-shared';
import { Font } from 'configurator-ui-shared';
import {
  setStyle,
  setPalette,
  setFont,
  setIcon,
  setLabel,
  setSlots,
  setMaterial,
  setTextFields,
  resetItem
} from 'configurator-ui-shared';

import { ParsedProductConfig } from '../types';

/**
 * A snapshot of the item slice's customisation state for a single product.
 * Captured when the user switches away from a product so it can be restored later.
 */
export interface ItemSnapshot {
  style: Style | null;
  palette: Palette | null;
  font: Font | null;
  icon: Icon | null;
  labelShape: Label | null;
  fields: ItemProperties['fields'];
  slots: Record<string, ItemSlot>;
  clothingType: Material | null;
}

export interface MultiProductState {
  products: ParsedProductConfig[];
  activeProductTag: string | null;
  snapshots: Record<string, ItemSnapshot>;
}

const initialState: MultiProductState = {
  products: [],
  activeProductTag: null,
  snapshots: {}
};

export const multiProductSlice = createSlice({
  name: 'multiProduct',
  initialState,
  reducers: {
    /** Populate the products list and set the initial active product on embed load. */
    initMultiProduct: (
      state,
      action: PayloadAction<{
        products: ParsedProductConfig[];
        defaultProductTag: string;
      }>
    ) => {
      state.products = action.payload.products;
      state.activeProductTag = action.payload.defaultProductTag;
    },

    /**
     * Save a customisation snapshot for a product tag.
     * Dispatched before switching away from a product.
     */
    saveSnapshot: (
      state,
      action: PayloadAction<{ productTag: string; snapshot: ItemSnapshot }>
    ) => {
      state.snapshots[action.payload.productTag] = action.payload.snapshot;
    },

    /**
     * Set the active product tag.
     * Snapshot save and state restore are handled by the switchProduct thunk.
     */
    setActiveProduct: (state, action: PayloadAction<string>) => {
      state.activeProductTag = action.payload;
    }
  }
});

export const { initMultiProduct, saveSnapshot, setActiveProduct } =
  multiProductSlice.actions;

// Minimal local state shape used by the switchProduct thunk to avoid a
// circular import with ./index (which derives RootState after this slice is registered).
interface ThunkState {
  multiProduct: MultiProductState;
  item: {
    properties: ItemProperties;
  };
}

/**
 * Thunk that orchestrates a full product switch:
 * 1. Snapshots the current item state under the outgoing product tag.
 * 2. Updates activeProductTag.
 * 3. Restores the snapshot for the incoming product (if one exists),
 *    or resets item state to defaults so the new product starts fresh.
 */
export const switchProduct =
  (newProductTag: string) =>
  (dispatch: (action: unknown) => void, getState: () => ThunkState) => {
    const state = getState();
    const { activeProductTag, snapshots } = state.multiProduct;

    // 1. Snapshot current item state under the outgoing product tag.
    if (activeProductTag && activeProductTag !== newProductTag) {
      const { properties } = state.item;
      const snapshot: ItemSnapshot = {
        style: properties.style,
        palette: properties.palette,
        font: properties.font,
        icon: properties.icon,
        labelShape: properties.labelShape,
        fields: properties.fields,
        slots: properties.slots,
        clothingType: properties.clothingType
      };
      dispatch(saveSnapshot({ productTag: activeProductTag, snapshot }));
    }

    // 2. Set new active product.
    dispatch(setActiveProduct(newProductTag));

    // 3. Restore prior snapshot or reset to defaults.
    // Always restore ALL fields unconditionally — null values must also be
    // written back so stale state from the outgoing product is cleared.
    const prior = snapshots[newProductTag];
    if (prior) {
      dispatch(setStyle(prior.style));
      dispatch(setPalette(prior.palette));
      dispatch(setFont(prior.font));
      dispatch(setIcon(prior.icon));
      dispatch(setLabel(prior.labelShape));
      dispatch(setMaterial(prior.clothingType));
      dispatch(setTextFields(prior.fields));
      for (const [slotId, value] of Object.entries(prior.slots)) {
        dispatch(setSlots({ slotId, value }));
      }
    } else {
      dispatch(resetItem());
    }
  };

export default multiProductSlice.reducer;
